import { API_CONFIG } from './config/api'
import { normalizeWalletAddress } from '../utils/addressUtils'
import { apiGet, apiPost } from '../shared/utils/apiClient'
import { UserTaskDtoStatusEnum } from '../types/generated'
import type {
  BonusStatsDto,
  UserTaskDto,
  UserTasksResponseDto,
  ReferralsResponseDto,
  ClaimRewardsResponseDto,
} from '../types/generated'

// ============= Types =============

// Re-export generated types for convenience
export type BonusStats = BonusStatsDto
export type BonusTask = UserTaskDto
export type UserTaskStatus = UserTaskDtoStatusEnum
export type TasksResponse = UserTasksResponseDto
export type TasksResponseBackend = UserTasksResponseDto
export type ReferralsResponse = ReferralsResponseDto
export type ClaimRewardsResponse = ClaimRewardsResponseDto

export interface ClaimRewardsRequest {
  walletAddress: string
}

// ============= API Functions =============

export async function getBonusStats(): Promise<BonusStats> {
  const response = await apiGet(API_CONFIG.endpoints.users.bonus.stats)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch bonus stats: ${response.status} ${response.statusText}`,
    )
  }

  try {
    return await response.json()
  } catch (e) {
    console.error('Failed to parse JSON response:', e)
    throw new Error('Invalid response format')
  }
}

export async function getUserTasks(
  walletAddress?: string,
): Promise<TasksResponseBackend> {
  const queryParams = new URLSearchParams({
    walletAddress: walletAddress ? normalizeWalletAddress(walletAddress) : '',
  })

  const response = await apiGet(
    `${API_CONFIG.endpoints.users.bonus.tasks}` +
      (walletAddress ? `?${queryParams.toString()}` : ''),
  )

  if (!response.ok) {
    throw new Error(
      `Failed to fetch user tasks: ${response.status} ${response.statusText}`,
    )
  }

  try {
    return await response.json()
  } catch (e) {
    console.error('Failed to parse JSON response:', e)
    throw new Error('Invalid response format')
  }
}

export async function getReferralUsers(
  limit: number = 50,
  offset: number = 0,
): Promise<ReferralsResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  })

  const response = await apiGet(
    `${API_CONFIG.endpoints.users.bonus.referrals}?${params.toString()}`,
  )

  if (!response.ok) {
    throw new Error(
      `Failed to fetch referral users: ${response.status} ${response.statusText}`,
    )
  }

  try {
    return await response.json()
  } catch (e) {
    console.error('Failed to parse JSON response:', e)
    throw new Error('Invalid response format')
  }
}

export async function claimRewards(
  request: ClaimRewardsRequest,
): Promise<ClaimRewardsResponse> {
  const normalizedRequest = {
    walletAddress: normalizeWalletAddress(request.walletAddress),
  }

  const response = await apiPost(
    API_CONFIG.endpoints.users.bonus.claim,
    normalizedRequest,
  )

  if (!response.ok) {
    throw new Error(
      `Failed to claim rewards: ${response.status} ${response.statusText}`,
    )
  }

  try {
    return await response.json()
  } catch (e) {
    console.error('Failed to parse JSON response:', e)
    throw new Error('Invalid response format')
  }
}

// ============= Helper Functions =============

/**
 * Calculate task status from completion data
 */
export function getTaskStatus(task: BonusTask): UserTaskStatus {
  if (task.status === UserTaskDtoStatusEnum.Completed) {
    return UserTaskDtoStatusEnum.Completed
  } else if (
    (task.progress?.current && task.progress.current > 0) ||
    task.status === UserTaskDtoStatusEnum.InProgress
  ) {
    return UserTaskDtoStatusEnum.InProgress
  } else {
    return UserTaskDtoStatusEnum.Pending
  }
}

/**
 * Format task progress as string (e.g., "3 из 5")
 */
export function formatTaskProgress(
  task: BonusTask,
  joinSymbol: string,
): string | null {
  if (!task.progress || task.progress.current === null) {
    return null
  }

  return `${task.progress.current} ${joinSymbol} ${task.progress.target}`
}
